import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUserAuth } from "context/UserContext";
import { studioApi } from "services/studioService";
import { errorText, formatDate } from "./studioText";

/* The Versions tab. Every AI turn that changed something is a version, and a
   restore is itself a new version, so nothing is ever lost by restoring. */

function StudioVersions({ projectId, currentVersion, busy, onRestored }) {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);

  const load = useCallback(() => {
    studioApi
      .listVersions(user, projectId)
      .then((data) => setVersions(data.versions))
      .catch((err) => setError(errorText(t, err)));
  }, [user, projectId, t]);

  useEffect(() => {
    load();
  }, [load, currentVersion]);

  const restore = async (n) => {
    if (!window.confirm(t("studio.versions.confirm", { n }))) return;
    setWorking(n);
    setError(null);
    try {
      await studioApi.restore(user, projectId, n);
      onRestored();
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setWorking(null);
    }
  };

  const download = async (n) => {
    setError(null);
    try {
      await studioApi.download(user, projectId, n);
    } catch (err) {
      setError(errorText(t, err));
    }
  };

  return (
    <div className="studio-scroll">
      <p className="studio-muted">{t("studio.versions.help")}</p>
      {error && <div className="studio-notice"><p>{error}</p></div>}
      <ul className="studio-versions">
        {versions.map((v) => (
          <li key={v.n} className="studio-version">
            <span className="studio-version__n">v{v.n}</span>
            <div className="studio-version__text">
              <div className="studio-version__summary">{v.summary || t(`studio.versions.source.${v.source}`, { defaultValue: v.source })}</div>
              <div className="studio-muted">
                {t(`studio.versions.source.${v.source}`, { defaultValue: v.source })} · {formatDate(v.createdAt, i18n.language)}
              </div>
            </div>
            {v.n === currentVersion ? (
              <span className="studio-chip studio-chip-done">{t("studio.versions.current")}</span>
            ) : (
              <button type="button" className="studio-btn studio-btn-ghost studio-btn-small" disabled={busy || working !== null} onClick={() => restore(v.n)}>
                {working === v.n ? t("studio.versions.restoring") : t("studio.versions.restore")}
              </button>
            )}
            <button type="button" className="studio-btn studio-btn-ghost studio-btn-small" onClick={() => download(v.n)}>
              {t("studio.versions.download")}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default StudioVersions;
